/**
 * Row with ticker
 */
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import { ITicker } from '@kattana-io/common';
import Text from '../../atoms/Text';
import useTickerLivePrice from './useTickerLivePrice';
import useTicker from '../../hooks/useTicker';
import PriceAlertCreate from './PriceAlertCreate';
import DailyPriceChange from './DailyPriceChange';
import Image from '../../atoms/Image';
import notice from '../../assets/images/notice.svg';
import flash from '../../assets/images/flash.svg';
import { createGetAlerts } from '../../selectors/priceAlerts';

const getAlertProps = (
  isFirst: boolean,
  hasAlerts: boolean,
  account: boolean
) => {
  const props: Record<string, string> = {};
  const classes = ['watchlist_row__alert', 'icon-alarm-add-alt'];

  if (isFirst) {
    props['data-tip'] = 'Set price<br />alert for this<br />pair';
    props['data-for'] = 'watchlist-first-item';
  }

  if (hasAlerts) {
    classes.push('watchlist_row__alert--with_alerts');
  }

  if (hasAlerts || isFirst) {
    classes.push('watchlist_row__alert--visible');
  } else {
    classes.push('watchlist_row__alert--hidden');
  }

  if (account) {
    classes.push('watchlist_row__alert--active');
  } else {
    classes.push('watchlist_row__alert--inactive');
  }

  return { ...props, className: classes.join(' ') };
};

interface IWatchlistRowProps {
  account: boolean;
  item: ITicker;
  actionSwitchCurrent: (payload: ITicker) => void;
  actionRemoveWatchlist?: (id: number) => void;
  actionAddWatchlist?: (item: ITicker) => void;
  active: boolean;
  onAccountRequired: () => void;
  isTrending?: boolean;
  isFirst?: boolean;
}

const WatchlistRow = ({
  account,
  item,
  actionSwitchCurrent,
  actionRemoveWatchlist,
  actionAddWatchlist,
  active,
  onAccountRequired,
  isTrending,
  isFirst,
}: IWatchlistRowProps) => {
  const selectAlerts = useMemo(() => createGetAlerts(item), [item]);
  const alerts = useSelector(selectAlerts);
  const [isPriceAlertModalVisible, setVisible] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const { name, isDEX } = useTicker(item);
  const { price, exchange, side } = useTickerLivePrice(item);

  const onPriceAlertClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (account) {
      setVisible(!isPriceAlertModalVisible);
    } else {
      onAccountRequired();
    }
  };

  const onSelect = useCallback(
    (e: any) => {
      e.stopPropagation();
      actionSwitchCurrent(item);
    },
    [item]
  );

  const onUnfav = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (ref.current) {
        (ref.current as HTMLElement).classList.add('watchlist_row--remove');
        setTimeout(() => {
          if (actionRemoveWatchlist) {
            actionRemoveWatchlist(item.id);
          }
        }, 150);
      }
    },
    [item]
  );

  const onFav = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (ref.current) {
        (ref.current as HTMLElement).classList.add('watchlist_row--remove');
        setTimeout(() => {
          if (actionAddWatchlist) {
            actionAddWatchlist(item);
          }
        }, 150);
      }
    },
    [item]
  );

  let priceColor = 'text--grey-200';
  if (side === 'buy') {
    priceColor = 'text--green-600';
  } else if (side === 'sell') {
    priceColor = 'text--red-600';
  }

  const rect = ref.current?.getBoundingClientRect();

  return (
    <div
      ref={ref}
      className={`watchlist_row ${active ? 'watchlist_row--active' : ''}`}
      onClick={onSelect}
    >
      <div className='row'>
        <div className='left'>
          <Text className='watchlist_row__exchange' size={12}>
            {exchange === 'maiar' ? 'xExchange' : exchange}
          </Text>
          <div className='watchlist_row__text watchlist_row__ellipses'>
            <Text className='text--grey-200' size={14} style={{ title: name }}>
              {name}
            </Text>
            {actionRemoveWatchlist && (
              <div className='unfavourite icon-star-active' onClick={onUnfav} />
            )}
            {actionAddWatchlist && (
              <div className='favourite icon-star' onClick={onFav} />
            )}
            {isTrending && (
              <div className='trend'>
                <Image src={flash} size='s' />
              </div>
            )}
          </div>
        </div>
        <div className='right'>
          <div className='watchlist_row__text watchlist_row__ellipses'>
            {isDEX && (
              <div
                onClick={onPriceAlertClick}
                {...getAlertProps(!!isFirst, alerts.length > 0, account)}
              >
                {isFirst && (
                  <Image
                    size='xxs'
                    src={notice}
                    className='watchlist_row__alert__notice'
                  />
                )}
              </div>
            )}
            <Text size={14} className={priceColor} style={{ title: price }}>
              {price}
            </Text>
          </div>
          {isFirst && (
            <ReactTooltip
              id='watchlist-first-item'
              type='light'
              place='bottom'
              multiline={true}
              className='watchlist_row__tooltip'
            />
          )}
          <DailyPriceChange ticker={item} price={price} />
          {isPriceAlertModalVisible && rect && (
            <PriceAlertCreate
              top={rect.top - 30}
              left={rect.left + 255}
              ticker={item}
              onClose={() => setVisible(false)}
              alerts={alerts}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WatchlistRow;
